import React from 'react';
import { string, object, mixed } from 'yup';
import {
  TextField,
  Typography,
  Grid,
  MenuItem,
  FormControlLabel,
  Checkbox,
  Radio,
  Switch,
} from '@material-ui/core';
import { SiteRole, User, AssignableRoles, getUserType, UserType } from '../../../models';
import { Formik, FormikHelpers } from 'formik';
import AllowForRoles from '../../modules/auth/AllowForRoles';
import { internationalPhone } from '../../../helpers/validation';
import { PrimaryButton, CancelButton } from '../../ui/Buttons';
import { pick } from 'lodash';
import { useSelector } from 'react-redux';
import { getUserProfile } from 'src/state/entities';

export const EditUserFormValidationSchema = object({
  name: string().min(0, 'Name is required.').required('Name is required.'),
  phoneNumber: internationalPhone({
    message: 'Enter country code followed by phone number: +1-999-999-9999',
    required: true,
  }),
  emailAddress: string().email().required('Email is required'),
  role: string().min(0, 'Role is required.').required('Role is required.'),
  userType: mixed().oneOf(Object.values(UserType)).required('User type is required'),
});

type FormValues = Pick<
  User,
  | 'name'
  | 'emailAddress'
  | 'phoneNumber'
  | 'isActive'
  | 'isSystemAdmin'
  | 'isCustomerAdmin'
  | 'isInstaller'
  | 'defaultSiteId'
  | 'userType'
> & { role: SiteRole };

export interface Props {
  user: User;
  siteId: number;
  onCancel: () => void;
  onSave: (values: User) => void;
}

const getRole = (user: User, siteId: number): SiteRole => {
  const siteRole = user.siteRoles.find((s) => s.siteId === siteId);
  return (siteRole && siteRole.role) || SiteRole.USER;
};

const getUserTypeFromValues = (values: Partial<FormValues>) => {
  const userTypeBooleans = pick(values, [
    'isSystemAdmin',
    'isCustomerAdmin',
    'isInstaller',
  ]) as Pick<User, 'isSystemAdmin' | 'isCustomerAdmin' | 'isInstaller'>;
  return getUserType(userTypeBooleans);
};

const EditUserForm: React.FC<Props> = (props) => {
  const { onSave, onCancel, user: siteUser, siteId } = props;
  const profile = useSelector(getUserProfile);
  const isSiteAdmin =
    profile?.sites.find((site) => site.siteId === siteId)?.role === SiteRole.SITE_ADMIN;

  const handleEditUser = ({ role, ...values }: FormValues, actions: FormikHelpers<FormValues>) => {
    const roleIdx = siteUser.siteRoles.findIndex(
      (siteAssignment) => siteAssignment.siteId === siteId
    );

    const siteRoles = [...siteUser.siteRoles];
    siteRoles[roleIdx] = {
      ...siteRoles[roleIdx],
      role,
    };

    const user: User = {
      ...siteUser,
      ...values,
      ...{
        phoneNumber: values.phoneNumber
          ? values.phoneNumber.replace(/\D/g, '')
          : values.phoneNumber,
      },
      siteRoles,
    };

    onSave(user);
    actions.setSubmitting(false);
  };

  const handleCancel = () => {
    onCancel();
  };

  const initialValues: FormValues = {
    name: siteUser.name,
    emailAddress: siteUser.emailAddress,
    isActive: siteUser.isActive,
    isCustomerAdmin: siteUser.isCustomerAdmin,
    isSystemAdmin: siteUser.isSystemAdmin,
    isInstaller: siteUser.isInstaller,
    phoneNumber: siteUser.phoneNumber || '',
    role: getRole(siteUser, siteId),
    defaultSiteId: siteUser.defaultSiteId,
    userType: getUserTypeFromValues(siteUser),
  };

  return (
    <Formik
      validationSchema={EditUserFormValidationSchema}
      onSubmit={handleEditUser}
      initialValues={initialValues}
      children={(formikProps) => {
        const {
          touched,
          errors,
          values,
          handleChange,
          handleBlur,
          handleSubmit,
          isSubmitting,
          isValidating,
          isValid,
          dirty,
          setFieldTouched,
          setFieldValue,
          setValues,
        } = formikProps;

        const handleUserTypeChange = (
          event: React.ChangeEvent<HTMLInputElement>,
          checked: boolean
        ) => {
          const optionValues = {
            isSystemAdmin: false,
            isInstaller: false,
            isCustomerAdmin: false,
          };
          optionValues[event.target.name] = checked;
          setValues({
            ...values,
            userType: getUserTypeFromValues(optionValues),
            ...optionValues,
          });
        };

        return (
          <form onSubmit={handleSubmit}>
            <Grid container direction="column" spacing={2} style={{ marginBottom: 0 }}>
              <Grid item>
                <Typography variant="h5">Edit User</Typography>
              </Grid>
              <Grid item>
                <TextField
                  fullWidth
                  variant="outlined"
                  size="small"
                  id="name"
                  name="name"
                  label="Name"
                  onChange={handleChange}
                  onBlur={handleBlur}
                  disabled={isSubmitting}
                  value={values.name}
                  error={touched.name && Boolean(errors.name)}
                  helperText={touched.name ? errors.name : null}
                />
              </Grid>
              <Grid item>
                <TextField
                  fullWidth
                  variant="outlined"
                  size="small"
                  id="phoneNumber"
                  name="phoneNumber"
                  label="Phone"
                  onKeyPress={(event: React.KeyboardEvent<HTMLDivElement>) => {
                    if (event.key.match(/[^0-9+\-()/.]/)) {
                      event.preventDefault();
                    }
                  }}
                  onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                    const rawValue = event.target.value;
                    if (rawValue) {
                      const sanitizedValue = rawValue.replace(/[^0-9+\-()/.]/g, '');
                      setFieldValue('phoneNumber', sanitizedValue, true);
                    } else {
                      setFieldValue('phoneNumber', rawValue, true);
                    }
                  }}
                  onBlur={handleBlur}
                  disabled={isSubmitting}
                  value={values.phoneNumber || ''}
                  error={touched.phoneNumber && Boolean(errors.phoneNumber)}
                  helperText={
                    touched.phoneNumber && Boolean(errors.phoneNumber)
                      ? errors.phoneNumber
                      : 'Enter country code followed by phone number: +1-999-999-9999'
                  }
                />
              </Grid>
              <Grid item>
                <TextField
                  data-testid={'edit-user-role-select'}
                  InputLabelProps={{
                    shrink: Boolean(values.role),
                  }}
                  fullWidth
                  variant="outlined"
                  size="small"
                  select
                  label="Role"
                  id="role"
                  name="role"
                  onChange={handleChange}
                  onBlur={() => {
                    setFieldTouched('role', true);
                  }}
                  disabled={isSubmitting}
                  value={values.role}
                  error={touched.role && Boolean(errors.role)}
                  helperText={touched.role ? errors.role : null}
                >
                  {AssignableRoles.map((role) => (
                    <MenuItem key={role} value={role}>
                      {role}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>
              <Grid item>
                {siteUser.defaultSiteId === siteId && (
                  <Typography variant="body1">This site is set as default</Typography>
                )}
                {siteUser.defaultSiteId !== siteId && (
                  <FormControlLabel
                    control={
                      <Checkbox
                        name="defaultSiteId"
                        onChange={(
                          event: React.ChangeEvent<HTMLInputElement>,
                          checked: boolean
                        ) => {
                          if (checked) {
                            setFieldValue('defaultSiteId', siteId, true);
                          } else {
                            setFieldValue('defaultSiteId', siteUser.defaultSiteId, true);
                          }
                        }}
                        color="primary"
                      />
                    }
                    label="Set this site as default"
                  />
                )}
              </Grid>

              {isSiteAdmin && (
                <Grid item>
                  <FormControlLabel
                    control={
                      <Switch
                        name="isActive"
                        checked={values.isActive}
                        onChange={handleChange}
                        color="primary"
                      />
                    }
                    label={values.isActive ? 'This user is active' : 'This user is inactive'}
                  />
                </Grid>
              )}

              <Grid item>
                <FormControlLabel
                  control={
                    <Switch
                      name="isActive"
                      checked={values.isActive}
                      onChange={handleChange}
                      color="primary"
                    />
                  }
                  label={values.isActive ? 'This user is active' : 'This user is inactive'}
                />
              </Grid>

              <Grid item container direction="row">
                <AllowForRoles siteId={siteId} systemAdmin={true}>
                  <Grid item xs={6}>
                    <FormControlLabel
                      htmlFor="isSystemAdmin"
                      control={
                        <Radio
                          inputProps={{
                            id: 'isSystemAdmin',
                          }}
                          name="isSystemAdmin"
                          checked={values.isSystemAdmin}
                          onChange={handleUserTypeChange}
                          color="primary"
                        />
                      }
                      label="System Admin"
                    />
                  </Grid>
                </AllowForRoles>

                <AllowForRoles siteId={siteId} systemAdmin={true} customerAdmin={true}>
                  <Grid item xs={6}>
                    <FormControlLabel
                      htmlFor="isCustomerAdmin"
                      control={
                        <Radio
                          inputProps={{
                            id: 'isCustomerAdmin',
                          }}
                          name="isCustomerAdmin"
                          checked={values.isCustomerAdmin}
                          onChange={handleUserTypeChange}
                          color="primary"
                        />
                      }
                      label="Customer Admin"
                    />
                  </Grid>
                </AllowForRoles>

                <AllowForRoles siteId={siteId} systemAdmin={true} customerAdmin={true}>
                  <Grid item xs={6}>
                    <FormControlLabel
                      htmlFor="isInstaller"
                      control={
                        <Radio
                          inputProps={{
                            id: 'isInstaller',
                          }}
                          name="isInstaller"
                          checked={values.isInstaller}
                          onChange={handleUserTypeChange}
                          color="primary"
                        />
                      }
                      label="Installer"
                    />
                  </Grid>
                </AllowForRoles>

                <AllowForRoles siteId={siteId} systemAdmin={true} customerAdmin={true}>
                  <Grid item xs={6}>
                    <FormControlLabel
                      htmlFor="isUser"
                      control={
                        <Radio
                          inputProps={{
                            id: 'isUser',
                          }}
                          checked={values.userType === UserType.USER}
                          onChange={handleUserTypeChange}
                          color="primary"
                        />
                      }
                      label="User"
                    />
                  </Grid>
                </AllowForRoles>
              </Grid>

              <Grid item container justifyContent="flex-end" spacing={1}>
                <Grid item style={{ marginRight: 'auto' }}>
                  <CancelButton onClick={handleCancel}>Cancel</CancelButton>
                </Grid>
                <Grid item>
                  <PrimaryButton
                    type="submit"
                    disabled={isSubmitting || isValidating || !isValid || !dirty}
                  >
                    Save
                  </PrimaryButton>
                </Grid>
              </Grid>
            </Grid>
          </form>
        );
      }}
    />
  );
};

export default EditUserForm;
